import { useEffect, useState } from "react";
import {
  Archive,
  Lock,
  Loader2,
  Download,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/tax";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function AdminArchives() {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));

  const load = () => {
    setLoading(true);
    api
      .get("/admin/archives")
      .then((res) => setArchives(res.data))
      .catch(() => toast.error("Failed to load archives"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const createArchive = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/archives", { year: parseInt(year) });
      toast.success(`Archive for ${year} created (locked)`);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadZip = async (a) => {
    setDownloading(a.id);
    try {
      const res = await api.get(`/admin/archives/${a.id}/export/zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const el = document.createElement("a");
      el.href = url;
      el.download = `cheltenham-archive-${a.year}.zip`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archive downloaded");
    } catch (e) {
      toast.error("Could not download archive");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
            <Archive className="w-6 h-6 text-[#1E3A8A]" /> Yearly Archives
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Permanent, read-only snapshots of a year's filings. Archives can never be deleted.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="create-archive-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Archive
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Create Yearly Archive</DialogTitle>
            </DialogHeader>
            <form onSubmit={createArchive} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Filing year</Label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  data-testid="archive-year-input"
                  min="2000"
                  max="2100"
                />
                <p className="text-xs text-slate-400">
                  Snapshots every filing submitted in this year. Once created it is locked
                  permanently.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full"
                  data-testid="archive-submit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Create &amp; Lock
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : archives.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="archives-empty">
          <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No archives yet. Create one to preserve a year's records.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archives-list">
          {archives.map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-md p-6" data-testid={`archive-${a.year}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-[#1E3A8A]" />
                  </span>
                  <div>
                    <p className="font-heading text-xl font-semibold text-slate-900">{a.year}</p>
                    <p className="text-xs text-slate-400">{a.filing_count} filing(s)</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <Lock className="w-3 h-3" /> Locked
                </span>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Assessed</span><span className="font-mono-data">{money(a.total_assessed)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Collected</span><span className="font-mono-data text-emerald-700">{money(a.total_collected)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="font-mono-data text-amber-700">{money(a.total_outstanding)}</span></div>
              </div>
              <Button
                variant="outline"
                className="w-full mt-4 border-slate-300"
                disabled={downloading === a.id}
                onClick={() => downloadZip(a)}
                data-testid={`archive-download-${a.year}`}
              >
                {downloading === a.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download records (ZIP)
              </Button>
              <p className="text-xs text-slate-400 mt-3">
                Archived {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""} by {a.created_by}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
